import { ArrowRight, Info } from "lucide-react";
import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface VarianceWeekMiniCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const FORECAST_COLOUR = "#6b7280";
const ACTUAL_COLOUR = "#111827";

// Cumulative forecast vs actual, one column per weekday
const SERIES = [
  { label: "Mon", forecast: 30, actual: 28 },
  { label: "Tue", forecast: 44, actual: 40 },
  { label: "Wed", forecast: 55, actual: 49 },
  { label: "Thu", forecast: 68, actual: 58 },
  { label: "Fri", forecast: 80, actual: 71 },
  { label: "Sat", forecast: 88, actual: 80 },
  { label: "Sun", forecast: 100, actual: 93 },
];

export default function VarianceWeekMiniCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: VarianceWeekMiniCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={460}
    >
      <Card className="w-[460px]">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-semibold text-neutral-900">
              Variance
            </span>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          {/* Week / Month pill toggle */}
          <div className="flex items-center rounded-full border border-border bg-muted/50 p-0.5 text-xs">
            <span className="rounded-full px-3 py-1 bg-background text-foreground font-semibold shadow-sm">
              Week
            </span>
            <span className="rounded-full px-3 py-1 text-muted-foreground">
              Month
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-5 flex flex-col gap-3">
          {/* KPI row */}
          <div className="flex items-center gap-8">
            <div>
              <div
                className="text-2xl font-bold tracking-tight"
                style={{ color: "var(--mc-error-icon)" }}
              >
                -6.7%
              </div>
              <p className="text-xs font-normal text-neutral-800 mt-0.5 whitespace-nowrap">
                variance this week
              </p>
            </div>

            <div className="w-px h-8 bg-border" />

            <div>
              <p
                className="text-2xl font-bold tracking-tight"
                style={{ color: FORECAST_COLOUR }}
              >
                £52,000
              </p>
              <p className="text-xs font-normal text-neutral-800 mt-0.5">
                Forecasted
              </p>
            </div>

            <div>
              <p
                className="text-2xl font-bold tracking-tight"
                style={{ color: ACTUAL_COLOUR }}
              >
                £48,500
              </p>
              <p className="text-xs font-normal text-neutral-800 mt-0.5">
                Actual
              </p>
            </div>
          </div>

          {/* Forecast vs actual chart — static stand-in for the real line chart */}
          <div className="flex gap-2">
            <div className="flex flex-col justify-between text-xs text-neutral-500 py-1">
              <span>£60K</span>
              <span>£30K</span>
              <span>£0K</span>
            </div>
            <div className="flex-1">
              <div className="h-[120px] flex items-end gap-2 border-b border-border">
                {SERIES.map((point) => (
                  <div
                    key={point.label}
                    className="flex-1 flex items-end justify-center gap-0.5"
                  >
                    <div
                      className="w-1.5 rounded-t"
                      style={{
                        height: `${point.forecast}%`,
                        backgroundColor: FORECAST_COLOUR,
                        opacity: 0.6,
                      }}
                    />
                    <div
                      className="w-1.5 rounded-t"
                      style={{
                        height: `${point.actual}%`,
                        backgroundColor: ACTUAL_COLOUR,
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-1">
                {SERIES.map((point) => (
                  <span
                    key={point.label}
                    className="flex-1 text-center text-xs text-neutral-500"
                  >
                    {point.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-border pt-3 flex justify-end">
            <span className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-neutral-900">
              View Reasons
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}
